import { CSSProperties, memo, useMemo } from 'react';

import { hasTerminalControls, parseAnsi } from './ansi';

// `pod@cluster:/path $`. The working directory never holds a `$` (cloud-server refuses one), so it ends the match.
const SHELL_PROMPT = /^(\S+@[^\s:$]+)(?::([^$]*))? \$/;

/** A prompt in the shell's colours, the way zsh paints one: who and where in green, the directory in blue. */
export const PromptText = memo(function PromptText({
  text,
}: Readonly<{ text: string }>) {
  const match = SHELL_PROMPT.exec(text);
  if (!match) return <>{text}</>;
  const [whole, host, directory] = match;
  return (
    <>
      <span className="CliTerminal__prompt-host">{host}</span>
      {directory === undefined ? null : (
        <>
          :<span className="CliTerminal__prompt-path">{directory}</span>
        </>
      )}{' '}
      <span className="CliTerminal__prompt-sigil">$</span>
      {text.slice(whole.length)}
    </>
  );
});

/** Output from the pod's TTY. Memoized, because a streaming line re-renders every flush and parsing it is not free. */
export const AnsiText = memo(function AnsiText({
  text,
}: Readonly<{ text: string }>) {
  const spans = useMemo(
    () => (hasTerminalControls(text) ? parseAnsi(text) : null),
    [text],
  );
  if (!spans) return <>{text}</>;
  return (
    <>
      {spans.map((span, index) => (
        <span
          key={index}
          className={span.className}
          style={
            span.color || span.background
              ? { color: span.color, background: span.background }
              : undefined
          }
        >
          {span.text}
        </span>
      ))}
    </>
  );
});

/** Tab completion's candidates, laid out in columns as a shell lists them rather than run together on one line. */
export function CompletionChoices({ items }: Readonly<{ items: string[] }>) {
  const widest = Math.max(...items.map(item => item.length));
  return (
    <div
      className="CliTerminal__line CliTerminal__line--notice CliTerminal__choices"
      style={{ '--choice-width': `${widest + 2}ch` } as CSSProperties}
    >
      {items.map(item => (
        <span key={item}>{item}</span>
      ))}
    </div>
  );
}
